import _, {uniq} from 'lodash';
import React, {FC, useRef} from 'react';
import styled from 'styled-components';

import {
  isAfterAction,
  isBeforeAction,
  KeyboardActionsEnum,
} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {ClickEventHandler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  KeyboardActionEventHandler,
  KeyboardEventHandlers,
} from '../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {buildIndexMap} from '../../../../srcCommon/helpers/itemIndexHelpers';
import {getSelectedIdsOnClick} from '../../../helpers/selectionHelpers';
import {CellsContext} from '../../../store/panels/cells/context/cellsContext';
import {InboxesVisualConversationSelection} from '../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  CellsPanelId,
  tryFindInboxesPanelIdInPanelId,
  ViewerPanelId,
} from '../../../store/panels/panelStateBase';
import {Tab} from '../../common/tabs/tab';
import {TabsWrapper} from '../../common/tabs/tabsWrapper';
import {P2InfoBubble} from '../../infoBubbles/p2InfoBubble';
import {
  CellsHeaderFiltersProps,
  Filter,
  makeFilterDefinitions,
  PaginatedFilterDefinition,
} from '../cells/header/cellsHeaderFilters';
import {SearchHintsBar} from '../search/hints/searchHintsBar';
import {InboxesTopbarActionsContainer} from './inboxesTopbarActionsContainer';

/*
 * Props.
 */

interface InboxesTopbarProps {
  panelId: ViewerPanelId;
  cellsPanelId: CellsPanelId | undefined;
  filters: CellsHeaderFiltersProps | undefined;
  cellsContext: CellsContext;
  parameters: InboxesParameters;
  conversationSelection: InboxesVisualConversationSelection;
  isInPopup: boolean;
}

/*
 * Style.
 */

const heightPx = 46;

const StyledWrapperDiv = styled.div<{$shouldWrapFilters: boolean}>`
  height: ${heightPx}px;
  /* Prevents the topbar from shrinking/growing in a popup where all elements are vertically stretched. */
  max-height: ${heightPx}px;
  min-height: ${heightPx}px;
  display: grid;
  grid-template-areas: '${(p) => (p.$shouldWrapFilters ? 'filters actions' : 'filters . actions')}';
  grid-template-columns: ${(p) => (p.$shouldWrapFilters ? '1fr auto' : 'min-content 1fr auto')};
  background-color: ${(p) => p.theme.foreground};
`;

const StyledTabsWrapper = styled(TabsWrapper)`
  grid-area: filters;
  flex: 1;
  padding-left: 16px;
  gap: 18px;
  padding-top: 15px;
`;

const StyledTab = styled(Tab)`
  margin-bottom: -4px;
`;

/*
 * Component.
 */

export const InboxesTopbar: FC<InboxesTopbarProps> = (props) => {
  const isInSearch =
    hasSearchInInboxesParameters(props.parameters) && props.parameters.search.query.length > 0;

  const onFilterKeyboardAction: KeyboardActionEventHandler = (_e, {action}) => {
    const {filters} = props;
    // Do we have the new filters, or filters at all?
    if (!filters) {
      return;
    }

    const {onMultipleFiltersSelect} = filters;

    // Do we have the required method?
    if (!onMultipleFiltersSelect) {
      return;
    }

    // Grab the selected filters.
    const lastSelectedFilter = _(filters.selectedFilters)
      // Sort them in the same order as the definitions
      .sortBy((f) => filters.filters.indexOf(f))
      // Take the last one
      .last();

    if (!lastSelectedFilter) {
      return;
    }

    // Where in the definition is our last filter?
    const lastSelectedFilterIndex = filters.filters.indexOf(lastSelectedFilter);

    const selectFilter = (filter: Filter) => {
      onMultipleFiltersSelect([filter]);
    };

    if (isAfterAction(action)) {
      const nextFilterIndex = (lastSelectedFilterIndex + 1) % filters.filters.length;
      const nextFilter = filters.filters[nextFilterIndex];

      selectFilter(nextFilter);
      return;
    }

    if (isBeforeAction(action)) {
      const previousFilterIndex =
        (lastSelectedFilterIndex + filters.filters.length - 1) % filters.filters.length;
      const previousFilter = filters.filters[previousFilterIndex];

      selectFilter(previousFilter);
    }
  };

  function renderFiltersPart() {
    if (!hasSearchInInboxesParameters(props.parameters)) {
      if (!props.filters) {
        return null;
      }

      const filterDefinitions = makeFilterDefinitions(props.filters);

      const keyboardHandlers: KeyboardEventHandlers = {
        [KeyboardActionsEnum.SELECT_NEXT_TAB]: onFilterKeyboardAction,
        [KeyboardActionsEnum.SELECT_PREVIOUS_TAB]: onFilterKeyboardAction,
      };

      return (
        <StyledTabsWrapper>
          <KeyboardShortcuts handlers={keyboardHandlers}>
            {filterDefinitions.map((filter) => (
              <StyledTab
                key={filter.id}
                isSelected={props.filters?.selectedFilters.includes(filter.id)}
                label={filter.content}
                onClick={props.filters ? makeOnFilterClick(filter, props.filters) : undefined}
                interactionId={filter.id}
              >
                {filter.content}
              </StyledTab>
            ))}
          </KeyboardShortcuts>
        </StyledTabsWrapper>
      );
    }

    const inboxesPanelId = tryFindInboxesPanelIdInPanelId(props.panelId);
    if (inboxesPanelId) {
      return <SearchHintsBar inboxesPanelId={inboxesPanelId} parameters={props.parameters} />;
    }

    return null;
  }

  const wrapperRef = useRef<HTMLDivElement>(null);
  return (
    <>
      <P2InfoBubble targetRef={wrapperRef} />
      <StyledWrapperDiv
        ref={wrapperRef}
        $shouldWrapFilters={isInSearch}
        {...buildInteractionIdProps('inboxes-topbar')}
      >
        {renderFiltersPart()}
        <InboxesTopbarActionsContainer
          panelId={props.panelId}
          cellsPanelId={props.cellsPanelId}
          cellsContext={props.cellsContext}
          inboxesParameters={props.parameters}
          conversationSelection={props.conversationSelection}
          isInPopup={props.isInPopup}
        />
      </StyledWrapperDiv>
    </>
  );
};

function makeOnFilterClick(
  filterDefinition: PaginatedFilterDefinition,
  props: CellsHeaderFiltersProps,
): ClickEventHandler {
  return (e) => {
    if (!filterDefinition.onMultipleFiltersSelect) {
      return;
    }

    const filterIds = props.filters;
    const filtersIndexes = buildIndexMap(filterIds);
    const newFilters = uniq(
      getSelectedIdsOnClick<Filter>(props.selectedFilters, filterIds, filtersIndexes, filterDefinition.id, e),
    );

    filterDefinition.onMultipleFiltersSelect(newFilters);
  };
}
